// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// COB-345: the email-capture panel for a provider-less Web3Auth sign-in.
// A Web3Auth login can yield a key but no email (external wallets, and
// sometimes X/Apple); the identity pubkey still resolves a returning
// account, but a brand-new one has nothing to register under. Rather than
// dead-ending, `loginWithWeb3Auth` stashes the live agent and throws
// `Web3AuthEmailRequiredError`; the chooser routes here to collect an email
// and finish registration on the COB-340 typed branch (server sends its own
// verification link).
//
// Modeled on `HardwareLogin`'s markup + validation, minus the device
// pre-warm — there is no WebHID prompt here, just an email field. Error
// discrimination is by `instanceof` on the session's exported error class.
//
// COB-272 (FIND-009): this panel fires the new-account register for the
// provider-less path, so it embeds the login-loss warning + acknowledgement
// checkbox from web3auth-loss-ack.tsx and gates submit on it — the
// provider-email path gets the same acknowledgement on its own panel.

import { useState } from 'preact/hooks';
import { useSession, Web3AuthEmailTakenError } from '../session-context.js';
import { validateEmail } from '../auth-flow.js';
import {
  Web3AuthLossWarning,
  WEB3AUTH_LOSS_ACK_LABEL,
} from './web3auth-loss-ack.js';
import '../styles/home.css';
import '../styles/accept.css';

/** Map a `completeWeb3AuthEmailRegistration` failure to user-facing copy.
 *  The one-account-per-email 409 (`Web3AuthEmailTakenError`) gets a bespoke
 *  line naming the fix; anything else passes its message through, falling
 *  back to a generic line. Exported for unit tests (mirrors
 *  `hardwareLoginErrorMessage`). */
export function web3authEmailCaptureErrorMessage(err: unknown): string {
  if (err instanceof Web3AuthEmailTakenError) {
    return (
      'That email is already attached to another Cobault account. If it’s ' +
      'yours, sign in with the method you used when you created it.'
    );
  }
  if (err instanceof Error) {
    return err.message || 'Could not finish signing in. Please try again.';
  }
  return 'Could not finish signing in. Please try again.';
}

export function Web3AuthEmailCapture({ onBack }: { onBack: () => void }) {
  const session = useSession();
  const [email, setEmail] = useState('');
  // COB-272: this panel fires the register for the provider-less path, so
  // the login-loss acknowledgement gates it here (the provider-email path
  // gets its own Web3AuthLossAck panel instead).
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailInvalid = validateEmail(email) !== null;

  async function handleSubmit(e?: Event) {
    if (e) e.preventDefault();
    // Enter-key implicit submission must not bypass the acknowledgement gate.
    if (!acknowledged) return;
    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await session.completeWeb3AuthEmailRegistration(email);
      // Success = authenticated. The parent page's post-auth gate unmounts
      // this panel; nothing more to do here.
    } catch (err) {
      setError(web3authEmailCaptureErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function handleBack() {
    // Tear down the stashed agent + half-committed session state before
    // returning to the chooser, so a fresh attempt starts clean.
    session.cancelWeb3AuthPending();
    onBack();
  }

  return (
    <div class="login-chooser login-chooser--minimal">
      <h2 class="login-chooser-title">One more thing</h2>
      <p class="accept-subtitle">
        Your sign-in worked, but it didn’t share an email address. Cobault
        needs one for your account — we’ll email you a link to confirm it’s
        yours.
      </p>

      <form onSubmit={handleSubmit}>
        <div class="accept-field">
          <label class="accept-label">Email</label>
          <input
            class="accept-input"
            type="email"
            autocomplete="email"
            placeholder="you@example.com"
            value={email}
            disabled={busy}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          />
          <p class="accept-input-hint">
            We’ll send a verification link to this address to confirm it’s
            yours.
          </p>
        </div>

        <Web3AuthLossWarning />

        <label class="loss-ack-row">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) =>
              setAcknowledged((e.target as HTMLInputElement).checked)
            }
            disabled={busy}
          />
          <span>{WEB3AUTH_LOSS_ACK_LABEL}</span>
        </label>

        {error && <p class="offer-error">{error}</p>}

        <div class="accept-actions">
          <button
            type="button"
            class="btn btn-secondary"
            onClick={handleBack}
            disabled={busy}
          >
            Back
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            disabled={busy || emailInvalid || !acknowledged}
            aria-busy={busy}
          >
            {busy ? 'Saving…' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
